import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import WomanIcon from "@mui/icons-material/Woman";
import ManIcon from "@mui/icons-material/Man";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { useLocation, useNavigate } from "react-router-dom";
import { Event } from "./Events";
import axios from "axios";
import { useUser } from "../UserContext";

interface CounterProps {
  label: string;
  maleCount: number;
  femaleCount: number;
  onMaleIncrement: () => void;
  onMaleDecrement: () => void;
  onFemaleIncrement: () => void;
  onFemaleDecrement: () => void;
}

const Counter: React.FC<CounterProps> = ({
  label,
  maleCount,
  femaleCount,
  onMaleIncrement,
  onMaleDecrement,
  onFemaleIncrement,
  onFemaleDecrement,
}) => (
  <Paper sx={{ p: 2, mb: 2 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
      }}
    >
      <Typography variant="h6">{label}</Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "grey.200",
          px: 2,
          py: 0.5,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "medium", color: "text.primary" }}
        >
          Total: {maleCount + femaleCount}
        </Typography>
      </Box>
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <ManIcon color="primary" />
          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            Male: {maleCount}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={onMaleDecrement} size="small" color="primary">
            <RemoveIcon />
          </IconButton>
          <IconButton onClick={onMaleIncrement} size="small" color="primary">
            <AddIcon />
          </IconButton>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <WomanIcon color="primary" />
          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            Female: {femaleCount}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={onFemaleDecrement} size="small" color="primary">
            <RemoveIcon />
          </IconButton>
          <IconButton onClick={onFemaleIncrement} size="small" color="primary">
            <AddIcon />
          </IconButton>
        </Box>
      </Grid>
    </Grid>
  </Paper>
);

interface ProductEntry {
  id: string;
  productId: number;
  productName: string;
  quantity: number;
}

interface SaleData {
  productEntries: ProductEntry[];
  gender: string;
  generation: string;
  ethnicity: string;
}

interface SaleMetadata {
  gender: string;
  generation: string;
  ethnicity: string;
}

interface Sale {
  products: {
    productId: number;
    productName: string;
    quantity: number;
  }[];
  metadata: SaleMetadata;
}

const Live: React.FC = () => {
  const location = useLocation();
  const eventData = location.state?.eventData as Event;
  const navigate = useNavigate();
  const { currentUser } = useUser();

  // Update products state to use event data
  const products = eventData?.products || [];

  // Counter states
  const [footTrafficMale, setFootTrafficMale] = useState(0);
  const [footTrafficFemale, setFootTrafficFemale] = useState(0);
  const [trialMale, settrialMale] = useState(0);
  const [trialFemale, settrialFemale] = useState(0);

  // Updated Modal state
  const [openModal, setOpenModal] = useState(false);
  const [saleData, setSaleData] = useState<SaleData>({
    productEntries: [
      {
        id: Math.random().toString(36).substr(2, 9),
        productId: products.length > 0 ? products[0].product_id : 0,
        productName: products.length > 0 ? products[0].product_name : "",
        quantity: 1,
      },
    ],
    gender: "",
    generation: "",
    ethnicity: "",
  });

  // Add new states for tracking sales
  const [totalSold, setTotalSold] = useState(0);
  const [salesHistory, setSalesHistory] = useState<Sale[]>([]);

  // Sample products (replace with actual data from events.products)
  const generations = ["Baby Boomer", "Gen X", "Millennial", "Gen Z"];
  const ethnicities = ["Asian", "Hispanic", "Black", "Other"];

  // Load data from Redis on component mount
  useEffect(() => {
    if (eventData?.event_id) {
      loadEventData();
    }
  }, [eventData?.event_id]);

  // Save data to Redis whenever it changes
  useEffect(() => {
    if (eventData?.event_id) {
      saveEventData();
    }
  }, [
    footTrafficMale,
    footTrafficFemale,
    trialMale,
    trialFemale,
    totalSold,
    salesHistory,
  ]);

  const loadEventData = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/events/live/${eventData.event_id}`
      );
      const data = response.data;

      setFootTrafficMale(parseInt(data.footTrafficMale) || 0);
      setFootTrafficFemale(parseInt(data.footTrafficFemale) || 0);
      settrialMale(parseInt(data.trialMale) || 0);
      settrialFemale(parseInt(data.trialFemale) || 0);
      setTotalSold(parseInt(data.totalSold) || 0);
      setSalesHistory(data.salesHistory || []);
    } catch (error) {
      console.error("Error loading event data:", error);
    }
  };

  const saveEventData = async () => {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/events/live/${eventData.event_id}`,
        {
          footTrafficMale,
          footTrafficFemale,
          trialMale,
          trialFemale,
          totalSold,
          salesHistory,
        }
      );
    } catch (error) {
      console.error("Error saving event data:", error);
    }
  };

  const handleSubmitSale = () => {
    const newSale: Sale = {
      products: saleData.productEntries.map((entry) => ({
        productId: entry.productId,
        productName: entry.productName,
        quantity: entry.quantity,
      })),
      metadata: {
        gender: saleData.gender,
        generation: saleData.generation,
        ethnicity: saleData.ethnicity,
      },
    };

    // Update total sold
    const saleQuantity = saleData.productEntries.reduce(
      (sum, entry) => sum + entry.quantity,
      0
    );
    setTotalSold((prev) => prev + saleQuantity);

    // Add to history
    setSalesHistory((prev) => [...prev, newSale]);

    // Reset form
    setOpenModal(false);
    setSaleData({
      productEntries: [
        {
          id: Math.random().toString(36).substr(2, 9),
          productId: 0,
          productName: "",
          quantity: 1,
        },
      ],
      gender: "",
      generation: "",
      ethnicity: "",
    });
  };

  const duplicateEntry = (index: number) => {
    const newEntry = {
      ...saleData.productEntries[index],
      id: Math.random().toString(36).substr(2, 9),
      productId: saleData.productEntries[index].productId,
    };
    setSaleData({
      ...saleData,
      productEntries: [
        ...saleData.productEntries.slice(0, index + 1),
        newEntry,
        ...saleData.productEntries.slice(index + 1),
      ],
    });
  };

  const removeEntry = (id: string) => {
    if (saleData.productEntries.length > 1) {
      setSaleData({
        ...saleData,
        productEntries: saleData.productEntries.filter(
          (entry) => entry.id !== id
        ),
      });
    }
  };

  // Add a Conversion display component
  const ConversionDisplay = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h6">Conversion</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
            size="small"
            sx={{
              ml: 2,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              height: 32,
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            Add Sale
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "grey.200",
            px: 2,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "medium", color: "text.primary" }}
          >
            Total Sold: {totalSold}
          </Typography>
        </Box>
      </Box>

      {/* Display recent sales */}
      <Box sx={{ mt: 2 }}>
        {salesHistory.map((sale, index) => (
          <Paper
            key={index}
            sx={{
              p: 1.5,
              mb: 1,
              backgroundColor: "background.default",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 0.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {sale.metadata.gender} • {sale.metadata.generation} •{" "}
                {sale.metadata.ethnicity}
              </Typography>
              <IconButton
                onClick={() => {
                  const newHistory = [...salesHistory];
                  const removedSale = newHistory.splice(index, 1)[0];
                  setSalesHistory(newHistory);
                  // Subtract the removed quantities from total
                  const removedQuantity = removedSale.products.reduce(
                    (sum, product) => sum + product.quantity,
                    0
                  );
                  setTotalSold((prev) => prev - removedQuantity);
                }}
                size="small"
                color="error"
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  padding: "4px",
                }}
              >
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
            {sale.products.map((product, pidx) => (
              <Typography key={pidx} variant="body2">
                {product.productName}: {product.quantity}
              </Typography>
            ))}
          </Paper>
        ))}
      </Box>
    </Paper>
  );

  // Add new function to handle event completion
  const handleCompleteEvent = async () => {
    if (!eventData?.event_id || !currentUser?.user_id) {
      console.log("Missing required data:", {
        eventId: eventData?.event_id,
        userId: currentUser?.user_id,
      });
      return;
    }

    setIsCompleting(true);
    try {
      await saveEventData();

      const payload = {
        salesHistory,
        footTrafficMale,
        footTrafficFemale,
        trialMale,
        trialFemale,
        userId: currentUser.user_id,
      };

      console.log("Sending payload:", payload);

      const url = `${import.meta.env.VITE_API_URL}/events/complete/${
        eventData.event_id
      }`;
      await axios.post(url, payload);

      setShowSuccessSnackbar(true);
      setTimeout(() => {
        navigate("/events");
      }, 2000);
    } catch (error) {
      console.error("Error completing event:", error);
      if (axios.isAxiosError(error)) {
        console.log("Response data:", error.response?.data);
      }
      setShowErrorSnackbar(true);
    } finally {
      setIsCompleting(false);
    }
  };

  const [isCompleting, setIsCompleting] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {eventData && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {eventData.event}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {eventData.event_desc}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(eventData.booking_date).toLocaleDateString()} •{" "}
            {eventData.account_json.account}
          </Typography>
        </Paper>
      )}

      <Counter
        label="Foot Traffic"
        maleCount={footTrafficMale}
        femaleCount={footTrafficFemale}
        onMaleIncrement={() => setFootTrafficMale((prev) => prev + 1)}
        onMaleDecrement={() =>
          setFootTrafficMale((prev) => Math.max(0, prev - 1))
        }
        onFemaleIncrement={() => setFootTrafficFemale((prev) => prev + 1)}
        onFemaleDecrement={() =>
          setFootTrafficFemale((prev) => Math.max(0, prev - 1))
        }
      />

      <Counter
        label="Trial"
        maleCount={trialMale}
        femaleCount={trialFemale}
        onMaleIncrement={() => settrialMale((prev) => prev + 1)}
        onMaleDecrement={() => settrialMale((prev) => Math.max(0, prev - 1))}
        onFemaleIncrement={() => settrialFemale((prev) => prev + 1)}
        onFemaleDecrement={() =>
          settrialFemale((prev) => Math.max(0, prev - 1))
        }
      />

      <ConversionDisplay />

      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCompleteEvent}
          disabled={isCompleting}
          startIcon={
            isCompleting ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {isCompleting ? "Completing Event..." : "Complete Event"}
        </Button>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={2000}
        message="Event successfully completed"
        onClose={() => setShowSuccessSnackbar(false)}
      />

      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowErrorSnackbar(false)}
        message="Failed to complete event. Please try again."
        ContentProps={{
          sx: { backgroundColor: "error.main" },
        }}
      />

      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth>
        <DialogTitle>Log Sale</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {saleData.productEntries.map((entry, index) => (
              <Paper
                key={entry.id}
                sx={{
                  p: 2,
                  mb: 2,
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <FormControl sx={{ flex: 2 }}>
                  <InputLabel>Product</InputLabel>
                  <Select
                    value={entry.productName}
                    onChange={(e) => {
                      const selectedProduct = products.find(
                        (p) => p.product_name === e.target.value
                      );
                      const newEntries = [...saleData.productEntries];
                      newEntries[index] = {
                        ...newEntries[index],
                        productName: e.target.value,
                        productId: selectedProduct
                          ? selectedProduct.product_id
                          : 0,
                      };
                      setSaleData({ ...saleData, productEntries: newEntries });
                    }}
                    label="Product"
                    fullWidth
                  >
                    {products
                      .filter(
                        (product) =>
                          product.product_name === entry.productName ||
                          !saleData.productEntries.some(
                            (e) =>
                              e.id !== entry.id &&
                              e.productName === product.product_name
                          )
                      )
                      .map((product) => (
                        <MenuItem
                          key={product.product_id}
                          value={product.product_name}
                        >
                          {product.product_name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Quantity"
                  type="number"
                  value={entry.quantity}
                  onChange={(e) => {
                    const newEntries = [...saleData.productEntries];
                    newEntries[index] = {
                      ...newEntries[index],
                      quantity: Math.max(1, parseInt(e.target.value)),
                    };
                    setSaleData({ ...saleData, productEntries: newEntries });
                  }}
                  InputProps={{
                    inputProps: { min: 1 },
                  }}
                  sx={{ flex: 1 }}
                />

                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    onClick={() => duplicateEntry(index)}
                    color="primary"
                    size="small"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                  {saleData.productEntries.length > 1 && (
                    <IconButton
                      onClick={() => removeEntry(entry.id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Gender</InputLabel>
              <Select
                value={saleData.gender}
                onChange={(e) =>
                  setSaleData({ ...saleData, gender: e.target.value })
                }
                label="Gender"
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Generation</InputLabel>
              <Select
                value={saleData.generation}
                onChange={(e) =>
                  setSaleData({ ...saleData, generation: e.target.value })
                }
                label="Generation"
              >
                {generations.map((gen) => (
                  <MenuItem key={gen} value={gen}>
                    {gen}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Ethnicity</InputLabel>
              <Select
                value={saleData.ethnicity}
                onChange={(e) =>
                  setSaleData({ ...saleData, ethnicity: e.target.value })
                }
                label="Ethnicity"
              >
                {ethnicities.map((ethnicity) => (
                  <MenuItem key={ethnicity} value={ethnicity}>
                    {ethnicity}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button onClick={handleSubmitSale} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Live;
